import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, UPLOADS_API_URL } from './config';
import AdministrationDashboard from './AdministrationDashboard'; 
import Swal from 'sweetalert2';

const CompanyFullInformation = () => {
  const { id } = useParams(); 
  const [company, setCompany] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/companysetup/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch company details');
        }
        const data = await response.json();
        setCompany(data);
        await fetchBranches(data.company_id); // Fetch branches using company_id
      } catch (error) {
        console.error('Error fetching company details:', error);
        setCompany(null);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchBranches = async (companyId) => {
      try {
        const response = await fetch(`${API_URL}/branch/company/${companyId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch branches');
        }

        const branchData = await response.json();
        setBranches(branchData);
      } catch (error) {
        console.error('Error fetching branches:', error);
        Swal.fire({
          title: 'Error!',
          text: error.message,
          icon: 'error',
        });
      }
    };

    fetchCompanyDetails();
  }, [id]);

  // Loading state
  if (isLoading) {
    return (
      <AdministrationDashboard>
        <div className="text-center my-5">
          <h2>Loading company information...</h2>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </AdministrationDashboard>
    );
  }

  // If no company data is found
  if (!company) {
    return (
      <AdministrationDashboard>
        <div className="text-center my-5">
          <h2>No company information available.</h2>
        </div>
      </AdministrationDashboard>
    );
  }

  return (
    <AdministrationDashboard>
      <div className="container my-5">
        <h1 className="text-center mb-4">{company.name}</h1>
        <div className="text-center mb-4">
          {company.portrait_logo ? (
            <img
              src={`${UPLOADS_API_URL}/${company.portrait_logo.replace(/\\/g, "/")}`}
              alt={`${company.name} Logo`}
              style={{ width: '150px', height: 'auto', objectFit: 'contain' }}
            />
          ) : (
            <div style={{ width: '150px', height: '150px', backgroundColor: '#ccc', margin: 'auto' }} />
          )}
        </div>

        {/* Company Information Cards */}
        <div className="row">
          <div className="col-md-6 mb-4 text-start">
            <div className="card" style={{ padding: '16px', border: '1px solid #ccc' }}>
              <h5>Basic Information</h5>
              <p><strong>Address:</strong> {company.address || 'N/A'}</p>
              <p><strong>Industry:</strong> {company.industry || 'N/A'}</p>
              <p><strong>Phone:</strong> {company.phone_number1 || 'N/A'}</p>
            </div>
          </div>
          <div className="col-md-6 mb-4 text-start">
            <div className="card" style={{ padding: '16px', border: '1px solid #ccc' }}>
              <h5>Additional Information</h5>
              <p><strong>Email:</strong> {company.email1 || 'N/A'}</p>
              <p><strong>Registration Number:</strong> {company.registration_number || 'N/A'}</p>
              <p><strong>Website:</strong> {company.website || 'N/A'}</p>
              <p><strong>Company Size:</strong> {company.company_size || 'N/A'}</p>
              <p><strong>Additional Notes:</strong> {company.additional_notes || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Branches List */}
        <h3 className="text-center mb-4">Branches</h3>
        <div className="row text-start">
          {branches.length > 0 ? branches.map(branch => (
            <div className="col-md-4 mb-4" key={branch.branch_id}>
              <div className="card" style={{ padding: '16px', border: '1px solid #ccc' }}>
                <h5>{branch.branch_name}</h5>
                <p><strong>BranchId:</strong> {branch.branch_id || 'N/A'}</p>
                <p><strong>Location:</strong> {branch.branch_location || 'N/A'}</p>
                <p><strong>City:</strong> {branch.branch_city || 'N/A'}</p>
                <p><strong>Notes:</strong> {branch.branch_location_notes || 'N/A'}</p>
              </div>
            </div>
          )) : (
            <div className="col-md-12 text-center">
              <p>No branches found for this company.</p>
            </div>
          )}
        </div>
      </div>
    </AdministrationDashboard>
  );
};

export default CompanyFullInformation;